"use client";

import { ReactNode, useEffect, useState } from "react";
import { AppRoot } from "@telegram-apps/telegram-ui";
import { AuthGate } from "./AuthGate";
import { ChunkRecovery } from "./ChunkRecovery";
import { bg } from "@/lib/tokens/semantic";
import { getTheme, type ThemeName } from "@/lib/theme";

interface ClientProvidersProps {
  children: ReactNode;
}

type TguiPlatform = "ios" | "base";

function getInitialPlatformClient(): TguiPlatform {
  const p = window.Telegram?.WebApp?.platform ?? "";
  return p === "ios" || p === "macos" ? "ios" : "base";
}

/**
 * Client-side providers wrapper
 * AppRoot enables @telegram-apps/telegram-ui components + syncs appearance
 * AuthGate checks Telegram user ID before rendering
 *
 * platform is resolved explicitly after mount so that window.Telegram.WebApp
 * is guaranteed to be initialised by the time AppRoot first renders its children.
 * Without this, getInitialPlatform() inside AppRoot reads an uninitialised bridge
 * and returns 'base', leaving Section/Cell without ios styles until a re-render.
 */
export function ClientProviders({ children }: ClientProvidersProps) {
  const [mounted, setMounted] = useState(false);
  const [appearance, setAppearance] = useState<ThemeName>("dark");
  const [platform, setPlatform] = useState<TguiPlatform>("base");

  useEffect(() => {
    setMounted(true);
    setAppearance(getTheme());
    setPlatform(getInitialPlatformClient());
    // EventSource не умеет слать заголовки — кладём initData в cookie, чтобы
    // middleware авторизовал owner-only SSE (/api/stream, /api/flow/*).
    const initData = window.Telegram?.WebApp?.initData || "";
    if (initData) {
      document.cookie =
        `tg_init_data=${initData}; path=/; SameSite=Lax; Max-Age=86400`;
    }
    // Watch for data-theme changes on <html>
    const obs = new MutationObserver(() => {
      const t = document.documentElement.getAttribute("data-theme") as ThemeName;
      if (t) setAppearance(t);
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <ChunkRecovery />
      {!mounted ? (
        <div style={{ minHeight: "100dvh", background: bg.base }} />
      ) : (
        <AppRoot platform={platform} appearance={appearance}>
          <AuthGate>{children}</AuthGate>
        </AppRoot>
      )}
    </>
  );
}
